import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, FileText, Loader2, CheckCircle2, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function SignupPage() {
  const { signUp, user } = useAuth();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState(false);

  // Already logged in → go straight to dashboard
  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const pwdRules = [
    { label: 'At least 8 characters', pass: password.length >= 8 },
    { label: 'Contains a number',     pass: /\d/.test(password) },
    { label: 'Passwords match',       pass: confirm.length > 0 && password === confirm },
  ];
  const allRulesMet = pwdRules.every(r => r.pass);
  const showRules   = password.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allRulesMet) {
      setError('Please make sure all password requirements are met.');
      return;
    }

    setLoading(true);
    const { data, error: signUpError } = await signUp(email.trim(), password, fullName.trim());
    setLoading(false);

    if (signUpError) {
      setError(signUpError.message || 'Could not create account. Please try again.');
      return;
    }

    // Email confirmation is disabled — session is available immediately
    if (data?.session) {
      setSuccess(true);
      setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
    } else {
      // Fallback: email confirmation still on — guide them
      setError('Please check your inbox and confirm your email to continue.');
    }
  };

  const INPUT = "w-full px-3.5 py-2.5 rounded-xl text-sm bg-[#F5F5F7] dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[#1D1D1F] dark:text-slate-100 placeholder:text-[#AEAEB2] focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-150";

  // ── Success screen ────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] dark:bg-slate-950 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-slate-200/60 dark:border-slate-700/60 px-8 py-12 text-center">
          <div className="flex justify-center mb-5">
            <div className="w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-[#1D1D1F] dark:text-slate-100">
            Welcome, {fullName.split(' ')[0]}!
          </h2>
          <p className="text-sm text-[#6E6E73] dark:text-slate-400 mt-2">
            Your account is ready. Taking you to your dashboard…
          </p>
          <div className="mt-6 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-sky-500" />
          </div>
        </div>
      </div>
    );
  }

  // ── Sign up form ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-slate-950 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-slate-200/60 dark:border-slate-700/60 px-8 py-10">

        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-sky-600 flex items-center justify-center shadow-md mb-4">
            <FileText className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-[22px] font-semibold text-[#1D1D1F] dark:text-slate-100 tracking-tight">
            Create account
          </h1>
          <p className="text-sm text-[#6E6E73] dark:text-slate-400 mt-1">Avexo AI — CA Statement Engine</p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full name */}
          <div>
            <label className="block text-[13px] font-medium text-[#1D1D1F] dark:text-slate-300 mb-1.5">
              Full name
            </label>
            <input
              type="text" autoComplete="name" required autoFocus
              value={fullName} onChange={e => setFullName(e.target.value)}
              placeholder="Ramesh Kumar"
              className={INPUT}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-[13px] font-medium text-[#1D1D1F] dark:text-slate-300 mb-1.5">
              Email address
            </label>
            <input
              type="email" autoComplete="email" required
              value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@cafirm.com"
              className={INPUT}
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-[13px] font-medium text-[#1D1D1F] dark:text-slate-300 mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'} autoComplete="new-password" required
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
                className={INPUT + ' pr-10'}
              />
              <button type="button" tabIndex={-1} onClick={() => setShowPwd(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E6E73] hover:text-slate-700 transition-colors">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Confirm password */}
          <div>
            <label className="block text-[13px] font-medium text-[#1D1D1F] dark:text-slate-300 mb-1.5">
              Confirm password
            </label>
            <input
              type={showPwd ? 'text' : 'password'} autoComplete="new-password" required
              value={confirm} onChange={e => setConfirm(e.target.value)}
              placeholder="Re-enter password"
              className={INPUT}
            />
          </div>

          {/* Password rules */}
          {showRules && (
            <div className="space-y-1.5 pt-0.5">
              {pwdRules.map(r => (
                <div key={r.label} className={`flex items-center gap-2 text-[12px] transition-colors ${r.pass ? 'text-emerald-600 dark:text-emerald-400' : 'text-[#AEAEB2] dark:text-slate-500'}`}>
                  <Check className={`w-3.5 h-3.5 flex-shrink-0 transition-opacity ${r.pass ? 'opacity-100' : 'opacity-30'}`} />
                  {r.label}
                </div>
              ))}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !fullName || !email || !password || !confirm}
            className="w-full mt-2 py-2.5 rounded-xl text-sm font-semibold bg-sky-600 hover:bg-sky-700 active:scale-[0.98] text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-150"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? 'Creating your account…' : 'Create account'}
          </button>
        </form>

        <p className="mt-6 text-center text-[13px] text-[#6E6E73] dark:text-slate-400">
          Already have an account?{' '}
          <Link to="/login" className="text-sky-600 dark:text-sky-400 font-medium hover:underline">
            Sign in
          </Link>
        </p>
      </div>
      <p className="mt-6 text-[12px] text-[#AEAEB2] dark:text-slate-600">Avexo AI</p>
    </div>
  );
}
